import React from 'react';
import { SignerAppReadyResponse } from '@dropbox/sign-internal-client';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@dropbox/dig-components/buttons';
import { Banner } from '@dropbox/dig-components/banner';
import { TextInput } from '@dropbox/dig-components/text_fields';
import {
  FormHelperText,
  FormLabel,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { Box } from '@dropbox/dig-foundations';
import { Text, Title, Link } from '@dropbox/dig-components/typography';
import styles from 'signer-app/signer-experience/sms-auth-number-v2.module.css';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { AuthPageLayout } from 'signer-app/signer-experience/auth-page-layout';

const msg = defineMessages({
  smsSendMsg: {
    id: '64c0f6a1659aa52911f8fa0926caa14048abb55750c5f8fbbf26ce236d009221',
    defaultMessage:
      "To make sure it's you, we need to verify via text message to",
    description: 'SMS auth send message',
  },
  sendCode: {
    id: '1d297e7777d96325f99d62694a03f20abb274c05097f1e718f819d80d7938d71',
    defaultMessage: 'Send code',
    description: 'Send SMS button',
  },
  enterCode: {
    id: 'c26a3cad8c828ff7fdd057f862c38e8cc74311fa7b50a437a754b984834dca57',
    defaultMessage: 'We just sent a text message to',
    description: 'Prompt to enter SMS code',
  },
  verify: {
    id: '2fb8b0a5ffafb105322d3d5b466c3461b3975223884a88ed958e85ae7dcc4373',
    defaultMessage: 'Verify & continue',
    description: 'Verify SMS code button',
  },
  attemptsLeft: {
    id: 'c6ebb5b81d89a1821c5de04b71b2623dbf67a0d131edde625cb82a2541aacef7',
    defaultMessage:
      "The authentication code you've entered is incorrect. {count, plural, one {# attempt} other {# attempts}} remaining.",
    description: 'Attempts left message',
  },
  maxAttempts: {
    id: '2541fb18617f561cf695a14561c5c1360b7496b473378b8096a0c45342ac0539',
    defaultMessage: 'You have reached the maximum number of attempts.',
    description: 'Out of attempts message',
  },
  contactSender: {
    id: '5b8c571273071066b47df6e9f79a2ff0ba543f14ab54258187cb9b16ad49c92b',
    defaultMessage: 'Contact {name} at {email}',
    description: 'Contact sender help text',
  },
  needHelp: {
    id: '2f7e86750bdbdd89f531ea3ed0b11048ecdfce2fdfd3350cf1cfc6275456cd4a',
    defaultMessage: 'Need help?',
    description: 'Need help heading',
  },
  invalidCode: {
    id: 'sms.invalid_code',
    defaultMessage: 'The code you entered is invalid. Please try again.',
    description: 'Error message for invalid SMS code',
  },
  enterCodeLabel: {
    id: 'sms.enter_code_label',
    defaultMessage: 'Enter code',
    description: 'Label for the SMS code input field',
  },
  rateLimited: {
    id: 'ada5aae49219fc05aed68589abb6ea4b6640932ceaa82da3b5c121dabf52798d',
    defaultMessage:
      'This phone number has been contacted too many times. Please wait a few minutes and try again.',
    description: 'Rate limit error message for SMS verification',
  },
  failedToSend: {
    id: 'f34ac07e259169f2990362e676ea441ac57b22826cbb004ccab1eca85c061e57',
    defaultMessage: 'Failed to send SMS',
    description: 'Error message when SMS fails to send',
  },
  invalidCodeGeneric: {
    id: '48fd3e7c6161f92a3fef448f6fea3ca2b0338f31108812125b868f5f861086d7',
    defaultMessage: 'Invalid code',
    description: 'Generic error message when SMS code verification fails',
  },
});

interface Props {
  signerNumber: string;
  numAttemptsLeft: number | null;
  requesterName?: string | null;
  requesterEmail: string;
  onSendSms(): Promise<SignerAppReadyResponse>;
  onVerify(code: string): Promise<SignerAppReadyResponse>;
  onSuccess?(resp: SignerAppReadyResponse): void;
  isRateLimited?: boolean;
  isEmbedded?: boolean;
}

export const SmsAuthNumberV2: React.FC<Props> = ({
  signerNumber,
  numAttemptsLeft,
  requesterName,
  requesterEmail,
  onSendSms,
  onVerify,
  onSuccess,
  isRateLimited = false,
  isEmbedded = false,
}) => {
  const intl = useIntl();
  const [step, setStep] = React.useState<'send' | 'verify'>('send');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [attempts, setAttempts] = React.useState<number | null>(
    numAttemptsLeft,
  );
  const [rateLimited, setRateLimited] = React.useState<boolean>(isRateLimited);

  React.useEffect(() => {
    if (isRateLimited) {
      setRateLimited(true);
    }
  }, [isRateLimited]);

  React.useEffect(() => {
    setAttempts(numAttemptsLeft);
  }, [numAttemptsLeft]);

  const outOfAttempts = attempts !== null && attempts === 0;
  const showBanner = !!error || outOfAttempts || rateLimited;

  const bannerMessage = React.useMemo(() => {
    if (rateLimited) {
      return intl.formatMessage(msg.rateLimited);
    }
    if (outOfAttempts) {
      return intl.formatMessage(msg.maxAttempts);
    }
    return intl.formatMessage(msg.attemptsLeft, { count: attempts ?? 0 });
  }, [rateLimited, outOfAttempts, attempts, intl]);

  const sendMutation = useMutation({
    mutationFn: () => onSendSms(),
    onSuccess(resp) {
      const codeResp = resp?.error?.code;
      if (codeResp === 'sms_rate_limit') {
        setRateLimited(true);
      } else if (codeResp === 'sms_auth_required' || !codeResp) {
        setRateLimited(false);
        setAttempts(resp?.error?.data?.numAttemptsLeft ?? attempts);
        setStep('verify');
      }
      setError(null);
    },
    onError() {
      setError(intl.formatMessage(msg.failedToSend));
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (smsCode: string) => onVerify(smsCode),
    onSuccess(resp) {
      const codeResp = resp?.error?.code;
      if (!resp?.ready && codeResp === 'sms_auth_required') {
        setAttempts(resp?.error?.data?.numAttemptsLeft ?? attempts);
        setError(intl.formatMessage(msg.invalidCode));
      } else if (codeResp === 'sms_rate_limit') {
        setRateLimited(true);
        setError(null);
      } else if (resp?.ready) {
        setError(null);
        onSuccess?.(resp);
      }
    },
    onError() {
      setError(intl.formatMessage(msg.invalidCodeGeneric));
    },
  });

  const handleSend = () => {
    setError(null);
    sendMutation.mutate();
  };

  const handleVerify = () => {
    if (!code) {
      return;
    }
    setError(null);
    verifyMutation.mutate(code);
  };

  const loading = sendMutation.isLoading || verifyMutation.isLoading;

  return (
    <AuthPageLayout hideFooter={isEmbedded}>
      {showBanner && (
        <div className={styles.bannerWrapper}>
          <Banner type="alert">
            <Banner.Message>{bannerMessage}</Banner.Message>
          </Banner>
        </div>
      )}

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        rowGap="Micro Large"
        className={styles.content}
      >
        <Title weightVariant="emphasized">
          <FormattedMessage
            id="sms.verify_identity"
            defaultMessage="Verify identity"
            description="title text in signer flow, indicating signer will need to verify their identity"
          />
        </Title>
        <Text color="subtle" variant="label" className={styles.instructionText}>
          <FormattedMessage
            {...(step === 'send' ? msg.smsSendMsg : msg.enterCode)}
          />
        </Text>
        <Text isBold tagName="p" style={{ margin: 0 }}>
          {signerNumber}
        </Text>

        {step === 'send' ? (
          <Button
            variant="primary"
            size="large"
            fullWidth
            disabled={loading || outOfAttempts || rateLimited}
            isLoading={loading}
            onClick={handleSend}
          >
            <FormattedMessage {...msg.sendCode} />
          </Button>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleVerify();
            }}
            className={styles.content}
          >
            <FormRow>
              <FormLabel htmlFor="smsCode">
                <FormattedMessage {...msg.enterCodeLabel} />
              </FormLabel>
              {/* eslint-disable-next-line @dropbox/sign/form-helper-text-id -- aria-errormessage + label is sufficient */}
              <TextInput
                id="smsCode"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                size="large"
                isInvalid={!!error}
                aria-errormessage="sms-error-msg"
              />
              {error && (
                <FormHelperText id="sms-error-msg" variant="alert">
                  {error}
                </FormHelperText>
              )}
            </FormRow>
            <Box marginTop="Micro Large">
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                disabled={loading || outOfAttempts || rateLimited}
                isLoading={loading}
              >
                <FormattedMessage {...msg.verify} />
              </Button>
            </Box>
            {!rateLimited && (
              <Box
                marginTop="Micro Small"
                display="flex"
                justifyContent="center"
              >
                <Text>
                  <Link
                    href="#"
                    variant="primary"
                    hasNoUnderline={false}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSend();
                    }}
                  >
                    <FormattedMessage
                      id="sms.resend"
                      defaultMessage="Send the code again"
                      description="Resend code link"
                    />
                  </Link>
                </Text>
              </Box>
            )}
          </form>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        rowGap="Micro XSmall"
        className={styles.content}
      >
        <Text isBold variant="label" size="small">
          <FormattedMessage {...msg.needHelp} />
        </Text>
        <Text color="subtle">
          <FormattedMessage
            {...msg.contactSender}
            values={{
              name: requesterName || '',
              email: requesterEmail,
            }}
          />
        </Text>
      </Box>
    </AuthPageLayout>
  );
};
